<template>
    <div class="form-group" :class="{ 'form-group--no-spacing': noSpacing }">
        <div class="input-group" :class="{ 'input-group--error': error }">
            <button
                v-if="!disabled && chip.length > 0"
                type="button"
                @click="removeChip"
                class="remove"
                ref="removeBtn"
            >
                <svg-icon iconName="close" />
            </button>
            <div
                v-if="percentage"
                class="percentage"
                :class="{ 'percentage--hidden': !inputFocus && !filled }"
            >
                %
            </div>
            <div
                v-if="type === 'price'"
                class="currency"
                :class="{ 'currency--hidden': !inputFocus && !filled }"
                ref="price"
            >
                $
            </div>
            <input
                :type="inputType"
                :value="chip || value"
                :class="{
                    filled,
                    dropdown,
                    showIcon,
                    passIcon: type === 'password',
                    inverse,
                }"
                :id="getInputAttribute(name)"
                :name="getInputAttribute(name)"
                ref="input"
                :step="subDollar || step ? 0.01 : 1"
                :required="required"
                :disabled="disabled || chip.length > 0"
                v-on:animationstart="checkAutofill"
                v-mask="'###-###-####'"
                :masked="true"
                @input="onInput"
                @keydown="keyHandler"
                @focus="toggleInputFocus(true)"
                @blur="toggleInputFocus(false)"
                autocomplete="off"
            />
            <InputLabel
                :active="chip.length > 0 || inputFocus || filled"
                :name="name"
                :error="error"
                :disabled="disabled"
                @clicked="labelClicked"
                >{{ label }}</InputLabel
            >
            <div v-if="showIcon && chip.length <= 0" class="icon">
                <svg-icon :iconName="icon" />
            </div>
            <button
                v-if="!disabled && type === 'password'"
                type="button"
                class="show-pass"
                @click="toggleShowPassword"
            >
                <svg-icon :iconName="passwordIcon" />
            </button>
        </div>
        <InputHint
            v-if="!hideHintError"
            :error="error"
            :hint="hint"
            :required="required"
            :showOptional="showOptional"
        />
    </div>
</template>

<script>
import InputHint from "./InputHint";
import InputLabel from "./InputLabel";
import { KEY_CODES } from "../utils/constants";

export default {
  name: "Phone",
  components: {
    InputHint,
    InputLabel,
  },
  props: {
    type: {
      type: String,
      default: "text",
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    hint: String,
    error: String,
    subDollar: {
      type: Boolean,
      default: false,
    },
    percentage: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    showOptional: {
      type: Boolean,
      default: false,
    },
    noSpacing: {
      type: Boolean,
      default: false,
    },
    dropdown: {
      type: Boolean,
      default: false,
    },
    hideHintError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    chip: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    inverse: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Boolean,
      default: false,
    },
    has_focus: {
      type: Boolean,
      default: false,
    },
    format: String,
  },
  mounted() {
    if (this.focus) this.setFocus();
  },
  computed: {
    filled() {
      return this.autofilled || this.value.length > 0;
    },
    passwordIcon() {
      return this.showPassword ? "eye-off" : "eye";
    },
    showIcon() {
      return this.icon.length > 0;
    },
    inputType() {
      if (this.type === "password") {
        return this.showPassword ? "text" : "password";
      } else if (this.type === "price") {
        return "text";
      }
      return this.type;
    },
  },
  data() {
    return {
      autofilled: false,
      inputFocus: this.focus,
      showPassword: false,
    };
  },
  methods: {
    checkAutofill({ animationName }) {
      if (animationName.includes("onAutoFillStart")) {
        this.autofilled = true;
      } else if (animationName.includes("onAutoFillCancel")) {
        this.autofilled = false;
      }
    },
    keyHandler(e) {
      if (this.has_focus) {
        if (e.keyCode === KEY_CODES.ESCAPE) {
          e.preventDefault();
          e.stopPropagation();
          return this.blurSuggest();
        }
      }

      if (
        this.dropdown &&
                (e.keyCode === KEY_CODES.ENTER ||
                    e.keyCode === KEY_CODES.UP ||
                    e.keyCode === KEY_CODES.DOWN)
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (e.keyCode === KEY_CODES.ENTER) {
          return this.$emit("dropdown-select");
        }
        return this.$emit("arrow-move", e.keyCode === KEY_CODES.UP);
      }
    },
    onInput(e) {
      if (this.chip.length === 0) {
        const { value } = e.target;
        this.$emit("input", value);
        if (this.$el) {
          this.$el.dispatchEvent(new Event("input"));
        }
      }
    },
    removeChip() {
      this.$emit("remove-chip");
      this.$nextTick(() => {
        this.$refs.input.focus();
      });
    },
    setFocus() {
      if (this.chip.length > 0) {
        this.$refs.removeBtn.focus();
      } else {
        this.$refs.input.focus();
      }
    },
    toggleInputFocus(toggle) {
      this.inputFocus = toggle;
      this.$emit(toggle ? "focus" : "blur");
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },
    blurSuggest() {
      this.$refs.input.blur();
      this.toggleInputFocus(false);
      this.$emit("remove-chip");
    },
    getInputAttribute(attribute) {
      return attribute;
    },
    labelClicked() {
      this.setFocus();
    },
  },
  watch: {
    chip(newVal) {
      if (newVal.length > 0) {
        this.$nextTick(() => {
          this.$refs.removeBtn.focus();
        });
      }
    },
    value(newVal, oldVal) {
      if (newVal.length) {
        if (this.subDollar) {
          this.$emit("add-comma");
          let pos = this.$refs.input.selectionStart;
          if (newVal.length !== oldVal.length) {
            pos = pos + 1;
          }
          this.$nextTick(() => {
            this.$refs.input.setSelectionRange(pos, pos);
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
@keyframes onAutoFillStart {
    from {
        /**/
    }
    to {
        /**/
    }
}
@keyframes onAutoFillCancel {
    from {
        /**/
    }
    to {
        /**/
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: $padding-xl;

    &--no-spacing {
        margin-bottom: 0;
    }

    .input-group {
        display: flex;
        flex-direction: column;
        width: 100%;
        position: relative;

        .remove,
        .icon {
            width: 24px;
            height: 24px;
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;

            .svg {
                color: $label-gray;
                stroke: $label-gray;
            }
        }

        .remove {
            cursor: pointer;
            outline: 0;
            padding: 0;
            border: none;

            &:focus {
                .svg {
                    color: $blue-navy;
                    stroke: $blue-navy;
                }
            }

            & + input {
                padding-right: 46px;
            }
        }

        .icon {
            pointer-events: none;
        }

        .show-pass {
            width: 36px;
            height: 36px;
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            padding: 0;
            background: none;
            border: none;
            outline: 0;
            cursor: pointer;

            .svg {
                color: $label-gray;
                stroke: $label-gray;
            }

            &:focus {
                .svg {
                    color: $blue-navy;
                    stroke: $blue-navy;
                }
            }
        }

        .currency {
            position: absolute;
            left: 0;
            top: 0;
            padding: $padding-input-top $padding-input-left
                $padding-input-bottom;

            font-size: $font-size-md;
            font-weight: 400;
            color: $black;

            &--hidden {
                display: none;
            }

            & + input {
                padding-left: calc(#{$padding-input-left} + 10px);
            }
        }

        .percentage {
            width: 24px;
            height: 24px;
            position: absolute;
            right: 5px;
            top: 55%;
            transform: translateY(-50%);
            background: none;
            background-size: contain;
            background-position: center;
            background-repeat: no-repeat;

            &--hidden {
                display: none;
            }

            &:focus {
                color: $blue-navy;
                stroke: $blue-navy;
            }

            & + input {
                padding-right: 46px;
            }
        }

        input {
            width: 100%;
            background-color: $white;
            color: $black;
            outline: 0;
            border: none;
            padding: $padding-input-top $padding-input-left
                $padding-input-bottom;

            font-size: $font-size-md;
            font-weight: 400;
            border-radius: $border-radius-md;
            border: 1px solid $border-divide;
            transition: $transition-simple-fast;
            box-sizing: border-box;
            caret-color: $blue-navy;
            box-shadow: none;

            &.dropdown {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }

            &.showIcon {
                padding-right: 46px;
            }

            &.passIcon {
                padding-right: 58px;
            }

            &:-webkit-autofill {
                animation-name: onAutoFillStart;
                -webkit-text-fill-color: $black !important;
            }
            &:not(:-webkit-autofill) {
                animation-name: onAutoFillCancel;
            }

            &:focus {
                border-color: $blue-navy;
                background-color: $white;

                & ~ .icon {
                    .svg {
                        color: $blue-navy;
                        stroke: $blue-navy;
                    }
                }
            }

            &.inverse {
                background-color: $white;
            }
        }

        &--error {
            input {
                border-color: $red-light;

                & ~ .icon {
                    .svg {
                        color: $red-active;
                        stroke: $red-active;
                    }
                }
            }
        }
    }
}
</style>
